// Analytics and Conversion Tracking via Google Tag Manager
// GTM ID: GTM-P2RPMRSL (already loaded in index.html)

// ============================================
// CONFIGURATION
// ============================================
export const ANALYTICS_CONFIG = {
  GTM_ID: 'GTM-P2RPMRSL',
  ENABLE_DEBUG: false // Set to true for console logging
};

// Initialize dataLayer
window.dataLayer = window.dataLayer || [];

// ============================================
// DATALAYER PUSH HELPER
// ============================================
const pushToDataLayer = (eventData) => {
  window.dataLayer.push(eventData);
  
  if (ANALYTICS_CONFIG.ENABLE_DEBUG) {
    console.log('GTM DataLayer Push:', eventData);
  }
};

// ============================================
// PAGE TRACKING
// ============================================

// Track page views (for SPAs)
export const trackPageView = (path, title) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: path,
    page_title: title,
    page_location: window.location.href
  });
};

// ============================================
// CONVERSION TRACKING
// ============================================

// Track phone call clicks
export const trackPhoneCall = (phoneNumber, location = 'unknown') => {
  pushToDataLayer({
    event: 'phone_call_click',
    event_category: 'Contact',
    event_label: phoneNumber,
    phone_number: phoneNumber,
    click_location: location,
    conversion_value: 10
  });
};

// Track form submissions
export const trackFormSubmission = (formName, formData = {}) => {
  pushToDataLayer({
    event: 'form_submission',
    event_category: 'Lead',
    event_label: formName,
    form_name: formName,
    service_type: formData.service_type || '',
    location: formData.location || '',
    conversion_value: 25
  });

  // Also push generate_lead (GA4 recommended event)
  pushToDataLayer({
    event: 'generate_lead',
    currency: 'USD',
    value: 25,
    lead_source: formName
  });
};

// Track quote requests
export const trackQuoteRequest = (service = 'General', location = 'unknown') => {
  pushToDataLayer({
    event: 'quote_request',
    event_category: 'Lead',
    event_label: service,
    service_type: service,
    request_location: location,
    conversion_value: 30
  });
};

// ============================================
// CONTENT TRACKING
// ============================================

// Track service page views
export const trackServiceView = (serviceName, serviceSlug) => {
  pushToDataLayer({
    event: 'view_item',
    event_category: 'Service',
    event_label: serviceName,
    item_id: serviceSlug,
    item_name: serviceName,
    item_category: 'Pest Control Service'
  });
};

// Track service area page views
export const trackServiceAreaView = (areaName, areaSlug) => {
  pushToDataLayer({
    event: 'view_service_area',
    event_category: 'Service Area',
    event_label: areaName,
    area_name: areaName,
    area_slug: areaSlug
  });
};

// Track blog post views
export const trackBlogView = (postTitle, postSlug, category) => {
  pushToDataLayer({
    event: 'view_blog_post',
    event_category: 'Blog',
    event_label: postTitle,
    post_title: postTitle,
    post_slug: postSlug,
    post_category: category
  });
};

// ============================================
// ENGAGEMENT TRACKING
// ============================================

// Track CTA button clicks
export const trackCTAClick = (ctaName, ctaLocation) => {
  pushToDataLayer({
    event: 'cta_click',
    event_category: 'CTA',
    event_label: ctaName,
    cta_name: ctaName,
    cta_location: ctaLocation
  });
};

// Track scroll depth
export const trackScrollDepth = (percentage) => {
  pushToDataLayer({
    event: 'scroll_depth',
    event_category: 'Engagement',
    event_label: `${percentage}%`,
    scroll_percentage: percentage
  });
};

// Track outbound link clicks
export const trackOutboundLink = (url, linkText) => {
  pushToDataLayer({
    event: 'outbound_link_click',
    event_category: 'Outbound',
    event_label: url,
    link_url: url,
    link_text: linkText
  });
};

// ============================================
// ECOMMERCE-STYLE TRACKING (for services)
// ============================================

// Track when user views service details
export const trackViewItemDetails = (service) => {
  pushToDataLayer({
    event: 'view_item',
    ecommerce: {
      items: [{
        item_id: service.slug,
        item_name: service.name,
        item_category: 'Pest Control',
        item_variant: service.category || 'General'
      }]
    }
  });
};

// Track when user starts contact process
export const trackBeginCheckout = (service) => {
  pushToDataLayer({
    event: 'begin_checkout',
    ecommerce: {
      items: [{
        item_id: service?.slug || 'general',
        item_name: service?.name || 'General Inquiry',
        item_category: 'Pest Control'
      }]
    }
  });
};

// ============================================
// INITIALIZATION (for compatibility)
// ============================================

export const initAllAnalytics = () => {
  // GTM is already loaded via index.html
  // This function exists for compatibility with existing code
  if (ANALYTICS_CONFIG.ENABLE_DEBUG) {
    console.log('GTM Analytics: Ready (GTM-P2RPMRSL)');
  }
  
  // Push initial page view
  trackPageView(window.location.pathname, document.title);
};

export const initGA4 = () => {
  // No-op - GA4 is managed through GTM
};

export const initFacebookPixel = () => {
  // No-op - Facebook Pixel should be added via GTM
};

// ============================================
// CUSTOM EVENT HELPER
// ============================================

export const trackEvent = (eventName, parameters = {}) => {
  pushToDataLayer({
    event: eventName,
    ...parameters
  });
};

export const trackFBEvent = (eventName, parameters = {}) => {
  // Facebook events go through GTM as well
  pushToDataLayer({
    event: `fb_${eventName.toLowerCase()}`,
    fb_event_name: eventName,
    ...parameters
  });
};

export default {
  initAllAnalytics,
  trackPageView,
  trackEvent,
  trackPhoneCall,
  trackFormSubmission,
  trackQuoteRequest,
  trackServiceView,
  trackServiceAreaView,
  trackBlogView,
  trackCTAClick,
  trackScrollDepth,
  trackOutboundLink,
  trackViewItemDetails,
  trackBeginCheckout,
  trackFBEvent
};
